/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import * as RadixUIDialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";

import { Durations } from "../../lib/enums/durations";
import { Screens } from "../../lib/enums/screens";
import { ZIndices } from "../../lib/enums/zIndices";
import { Overlay } from "./Overlay";

export type CardModalProps = {
  children: ReactNode;
  contentCss?: Interpolation<ThemeType>;
  isOpen: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onOpenChange: (open: boolean) => void;
};

const cssOverlay: Interpolation = {
  zIndex: ZIndices.Overlay,
};

const cssMotionDiv: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.background,
  color: theme.semanticColors.primary,
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  left: 0,
  overflow: "hidden",
  paddingBottom: theme.spacing.ms,
  paddingLeft: theme.spacing.l,
  paddingRight: theme.spacing.l,
  paddingTop: theme.spacing.ms,
  position: "fixed",
  top: 0,
  width: "100dvw",
  zIndex: ZIndices.Content,
  [`@media ${Screens.Large}`]: {
    borderRadius: theme.radius.l,
    height: "fit-content",
    left: "50%",
    maxWidth: 480,
    paddingBottom: theme.spacing.xl,
    paddingLeft: theme.spacing.xl,
    paddingRight: theme.spacing.xl,
    paddingTop: theme.spacing.l,
    top: "50%",
  },
});

export const CardModal = ({
  children,
  contentCss,
  isOpen,
  onOpenAutoFocus,
  onOpenChange,
}: CardModalProps) => {
  const isLarge = useMediaQuery(Screens.Large);
  const offset = isLarge ? "-50%" : "0";

  return (
    <RadixUIDialog.Root onOpenChange={onOpenChange} open={isOpen}>
      <RadixUIDialog.Portal>
        <RadixUIDialog.Overlay asChild>
          <Overlay css={cssOverlay} onClick={() => onOpenChange(false)} />
        </RadixUIDialog.Overlay>
        <AnimatePresence>
          {isOpen && (
            <RadixUIDialog.Content asChild onOpenAutoFocus={onOpenAutoFocus}>
              <motion.div
                animate={{ opacity: 1, scale: 1, x: offset, y: offset }}
                css={[cssMotionDiv, contentCss]}
                exit={{ opacity: 0, scale: 0.95, x: offset, y: offset }}
                initial={{ opacity: 0, scale: 0.95, x: offset, y: offset }}
                transition={{ duration: Durations.Long, type: "spring" }}
              >
                {children}
              </motion.div>
            </RadixUIDialog.Content>
          )}
        </AnimatePresence>
      </RadixUIDialog.Portal>
    </RadixUIDialog.Root>
  );
};
